import { useEffect, useRef, useState } from 'react';
import { cx } from '@/lib/cx';
import {
  AudioLab,
  describe,
  GAME,
  loadResults,
  newResult,
  PRESETS,
  presetConfig,
  resultsText,
  saveResults,
  sessionType,
  type Diagnostics,
  type LabConfig,
  type LabResult,
} from './audioLab';
import s from './debug.module.css';
import { Card, Seg } from './ui';
import { copyText, download, fmtTime, useToast } from './util';

/** Test lines: spliced like the game's practice lines, and long single recordings. */
const LINES = [
  { text: 'Can you say dinosaur?', label: 'Practice line, spliced: "Can you say dinosaur?"' },
  {
    text: 'try to say tornado, with a strong r.',
    label: 'Practice line, spliced: "Try to say tornado…"',
  },
  {
    text: 'Look at the pictures. They show the inside of a mouth, from the side. The red cross is the sound I heard, and the green tick is the r. Look at the tongue.',
    label: 'Coaching line, one 10 s recording',
  },
  {
    text: '[mysterious] But something strange is going on. Our snacks go missing, and our things get hidden. Some sneaky foes, troublemakers in disguise, keep playing tricks on us!',
    label: 'Story line, one 11 s recording',
  },
] as const;

type Rating = LabResult['rating'];

interface Field<K extends keyof LabConfig> {
  key: K;
  label: string;
  options: readonly LabConfig[K][];
}

const FIELDS = [
  {
    key: 'session',
    label: 'Audio session',
    options: ['game', 'untouched', 'auto', 'playback', 'play-and-record'],
  },
  { key: 'context', label: 'Context', options: ['shared', 'fresh-before-mic', 'fresh-after-mic'] },
  { key: 'sampleRate', label: 'Sample rate', options: ['default', '44100', '48000'] },
  {
    key: 'latencyHint',
    label: 'Latency hint',
    options: ['default', 'interactive', 'balanced', 'playback'],
  },
  { key: 'worklet', label: 'Capture worklet', options: ['before-mic', 'after-mic', 'none'] },
  { key: 'echoCancellation', label: 'Echo cancellation', options: ['on', 'off', 'exact-on'] },
  { key: 'noiseSuppression', label: 'Noise suppression', options: ['off', 'on', 'exact-off'] },
  { key: 'sound', label: 'Sound', options: ['line-game', 'line-plain', 'tone', 'element'] },
  {
    key: 'duringPlayback',
    label: 'Mic while playing',
    options: [
      'keep',
      'disable-track',
      'stop-track',
      'stop-track-playback',
      'release-all',
      'ec-off',
    ],
  },
  { key: 'level', label: 'Level (tone, plain)', options: ['0.03', '0.1', '0.25', '0.7'] },
  { key: 'capture', label: 'Capture', options: ['same-context', 'separate-context'] },
  {
    key: 'output',
    label: 'Output (tone, plain)',
    options: ['destination', 'stream-element', 'webrtc-loopback'],
  },
  {
    key: 'webAudio',
    label: 'Web Audio (audio element)',
    options: ['running', 'suspended', 'closed'],
  },
  {
    key: 'settleMs',
    label: 'Wait after the mic change (ms)',
    options: ['0', '300', '1000', '2000'],
  },
] as const satisfies readonly Field<keyof LabConfig>[];

const RATING_BADGE: Record<Rating, string | undefined> = {
  clean: s.badgeOk,
  crackly: s.badgeDanger,
  unsure: s.badgeWarn,
};

/**
 * The audio lab (issue #39): build one audio setup from independent choices, play a test sound
 * through it on the phone, rate it, and copy the results. The presets change one thing each from
 * the game's setup.
 */
export function AudioLabPanel() {
  const toast = useToast();
  const lab = useRef<AudioLab | null>(null);
  const [config, setConfig] = useState<LabConfig>(GAME);
  const [preset, setPreset] = useState('B');
  const [line, setLine] = useState<string>(LINES[0].text);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [last, setLast] = useState<{ diag: Diagnostics; config: LabConfig; preset: string } | null>(
    null,
  );
  const [note, setNote] = useState('');
  const [results, setResults] = useState<LabResult[]>(loadResults);
  const [confirmClear, setConfirmClear] = useState(false);
  const [micOpen, setMicOpen] = useState(false);
  /** The setup that was last built: "Play again" plays and records it, not the current choice. */
  const [built, setBuilt] = useState<{
    config: LabConfig;
    preset: string;
    diag: Diagnostics;
  } | null>(null);
  /** The line was switched for the audio element preset (switched back after it). */
  const autoLine = useRef(false);

  useEffect(
    () => () => {
      void lab.current?.teardown();
    },
    [],
  );

  const fresh = config.context !== 'shared';
  const ratedBy = (id: string) => results.findLast((r) => r.preset === id)?.rating;
  const nextUntested = PRESETS.find((p) => !ratedBy(p.id));

  const choose = (id: string) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPreset(id);
    setConfig(presetConfig(p));
    // The audio element plays one recorded range: a line recorded in one piece.
    if (p.config.sound === 'element') {
      autoLine.current = line !== LINES[2].text;
      setLine(LINES[2].text);
    } else if (autoLine.current) {
      autoLine.current = false;
      setLine(LINES[0].text);
    }
  };
  const change = <K extends keyof LabConfig>(key: K, value: LabConfig[K]) => {
    setConfig((c) => ({ ...c, [key]: value }));
    setPreset('custom');
  };

  /** Builds the setup and plays the sound (`rebuild` false: plays again on the current setup). */
  const run = (rebuild: boolean) => {
    lab.current ??= new AudioLab();
    const current = lab.current;
    setBusy(true);
    setStatus('starting…');
    void (async () => {
      try {
        let setup = built;
        if (rebuild || !setup) {
          setup = { config, preset, diag: await current.build(config, setStatus) };
          setBuilt(setup);
        }
        setMicOpen(current.micOpen);
        setStatus('playing…');
        const played = await current.play(setup.config.sound, line, setStatus);
        setLast({ diag: { ...setup.diag, ...played }, config: setup.config, preset: setup.preset });
        setStatus(`done — session ${sessionType()}`);
      } catch (e) {
        setStatus(`error: ${String(e)}`);
      } finally {
        setBusy(false);
      }
    })();
  };

  const rate = (rating: Rating) => {
    if (!last) return;
    const next = [
      ...results,
      newResult({
        preset: last.preset,
        config: last.config,
        line,
        rating,
        note: note.trim(),
        diag: last.diag,
      }),
    ];
    setResults(next);
    saveResults(next);
    setNote('');
    toast(`${last.preset}: ${rating}`);
    if (nextUntested?.id === last.preset) {
      const after = PRESETS.find(
        (p) => p.id !== last.preset && !next.some((r) => r.preset === p.id),
      );
      if (after) choose(after.id);
    }
  };

  const device = navigator.userAgent;

  return (
    <>
      <Card
        title="Setup"
        actions={
          <span className={cx(s.badge, micOpen && s.badgeDanger)}>
            {micOpen ? '● mic open' : 'mic closed'}
          </span>
        }
      >
        <p className={s.hint}>
          Pick a preset (each changes one thing from the game), tap Run, listen, then rate it. Use
          the phone's speaker at full volume. The tone makes crackle easiest to hear.
        </p>
        <div className={s.row} role="radiogroup" aria-label="Presets">
          {PRESETS.map((p) => {
            const rating = ratedBy(p.id);
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={preset === p.id}
                title={`${p.label}: ${p.why}`}
                className={cx(s.btn, s.small, preset === p.id && s.primary)}
                onClick={() => {
                  choose(p.id);
                }}
              >
                {p.id}
                {rating && (
                  <span className={cx(s.badge, RATING_BADGE[rating])}>
                    {rating === 'clean' ? '✓' : rating === 'crackly' ? '✕' : '?'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p style={{ margin: 0 }}>
          {preset === 'custom' ? (
            <strong>Custom setup</strong>
          ) : (
            <>
              <strong>
                {preset}. {PRESETS.find((p) => p.id === preset)?.label}
              </strong>{' '}
              <span className={s.hint}>{PRESETS.find((p) => p.id === preset)?.why}</span>
            </>
          )}
        </p>

        <details>
          <summary className={s.hint} style={{ cursor: 'pointer' }}>
            Variables ({describe(config)})
          </summary>
          <div className={s.kv} style={{ marginTop: 8, alignItems: 'center' }}>
            {FIELDS.map((f) => {
              const disabled = (f.key === 'sampleRate' || f.key === 'latencyHint') && !fresh;
              return (
                <FieldRow key={f.key} label={f.label}>
                  <select
                    className={s.select}
                    aria-label={f.label}
                    value={config[f.key]}
                    disabled={disabled}
                    onChange={(e) => {
                      change(f.key, e.target.value as LabConfig[typeof f.key]);
                    }}
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                  {disabled && <span className={s.hint}> new contexts only</span>}
                </FieldRow>
              );
            })}
            <FieldRow label="Microphone">
              <Seg
                label="Microphone"
                options={['on', 'off'] as const}
                value={config.mic ? 'on' : 'off'}
                onChange={(v) => {
                  change('mic', v === 'on');
                }}
              />
            </FieldRow>
            <FieldRow label="Auto gain">
              <Seg
                label="Auto gain"
                options={['on', 'off'] as const}
                value={config.autoGainControl ? 'on' : 'off'}
                onChange={(v) => {
                  change('autoGainControl', v === 'on');
                }}
              />
            </FieldRow>
          </div>
        </details>

        <label className={s.row}>
          <span className={s.label}>Test line</span>
          <select
            className={s.select}
            value={line}
            onChange={(e) => {
              autoLine.current = false;
              setLine(e.target.value);
            }}
            style={{ flex: '1 1 220px', maxWidth: '100%' }}
          >
            {LINES.map((l) => (
              <option key={l.text} value={l.text}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        <div className={s.row}>
          <button
            type="button"
            className={cx(s.btn, s.primary)}
            disabled={busy}
            onClick={() => {
              run(true);
            }}
          >
            ▶ Run setup
          </button>
          <button
            type="button"
            className={s.btn}
            disabled={busy || !built}
            onClick={() => {
              run(false);
            }}
          >
            Play again{built && built.preset !== preset ? ` (${built.preset})` : ''}
          </button>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              lab.current?.stop();
            }}
          >
            Stop
          </button>
          <button
            type="button"
            className={s.btn}
            disabled={busy}
            onClick={() => {
              void lab.current?.teardown().then(() => {
                setMicOpen(false);
                setBuilt(null);
                setStatus(`released — session ${sessionType()}`);
              });
            }}
          >
            Release mic
          </button>
          {status && <span className={cx(s.hint, s.mono)}>{status}</span>}
        </div>
      </Card>

      {last && (
        <Card title={`Rate run ${last.preset}`}>
          <dl className={s.kv}>
            {Object.entries(last.diag).map(([k, v]) => (
              <FieldRow key={k} label={k}>
                {v}
              </FieldRow>
            ))}
          </dl>
          <input
            className={s.input}
            placeholder="Note (optional): where it crackled, how often…"
            aria-label="Note for this run"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
          <div className={s.row}>
            <button
              type="button"
              className={cx(s.btn, s.primary)}
              style={{ background: 'var(--ok)', borderColor: 'var(--ok)' }}
              onClick={() => {
                rate('clean');
              }}
            >
              ✓ Clean
            </button>
            <button
              type="button"
              className={cx(s.btn, s.dangerSolid)}
              onClick={() => {
                rate('crackly');
              }}
            >
              ✕ Crackly
            </button>
            <button
              type="button"
              className={s.btn}
              onClick={() => {
                rate('unsure');
              }}
            >
              ? Unsure
            </button>
          </div>
        </Card>
      )}

      <Card
        title={`Results (${String(results.length)})`}
        actions={
          <div className={s.row}>
            <button
              type="button"
              className={cx(s.btn, s.small, s.primary)}
              disabled={results.length === 0}
              onClick={() => {
                void copyText(resultsText(results, device)).then((ok) => {
                  toast(ok ? 'Copied the results' : 'Copy failed');
                });
              }}
            >
              Copy as text
            </button>
            <button
              type="button"
              className={cx(s.btn, s.small)}
              disabled={results.length === 0}
              onClick={() => {
                download('foe-names-audiolab.json', JSON.stringify({ device, results }, null, 2));
              }}
            >
              JSON
            </button>
            {confirmClear ? (
              <>
                <button
                  type="button"
                  className={cx(s.btn, s.small, s.dangerSolid)}
                  onClick={() => {
                    setResults([]);
                    saveResults([]);
                    setConfirmClear(false);
                  }}
                >
                  Clear all
                </button>
                <button
                  type="button"
                  className={cx(s.btn, s.small)}
                  onClick={() => {
                    setConfirmClear(false);
                  }}
                >
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                className={cx(s.btn, s.small, s.danger)}
                disabled={results.length === 0}
                onClick={() => {
                  setConfirmClear(true);
                }}
              >
                Clear
              </button>
            )}
          </div>
        }
        flush
      >
        {results.length === 0 ? (
          <p className={s.empty}>
            No runs rated yet.{nextUntested && ` Start with ${nextUntested.id}.`}
          </p>
        ) : (
          <div className={s.tableWrap}>
            <table className={cx(s.table, s.stack)}>
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Rating</th>
                  <th>Changes from the game</th>
                  <th className={s.hideNarrow}>When</th>
                </tr>
              </thead>
              <tbody>
                {[...results].reverse().map((r) => (
                  <tr key={r.at}>
                    <td>
                      <strong>{r.preset}</strong>{' '}
                      <span className={s.hint}>
                        {PRESETS.find((p) => p.id === r.preset)?.label ?? 'custom'}
                      </span>
                    </td>
                    <td data-label="Rating">
                      <span className={cx(s.badge, RATING_BADGE[r.rating])}>{r.rating}</span>
                    </td>
                    <td data-label="Changes" className={s.mono}>
                      {describe(r.config)}
                      {r.note && <div className={s.hint}>{r.note}</div>}
                    </td>
                    <td data-label="When" className={s.hideNarrow}>
                      {fmtTime(r.at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
